/**
 * Authenticated client for the Course Registration module.
 * Backend: backend/routes/courseRegistration.js (mounted /api/course-registration).
 * The public self-registration page uses services/publicApi.js instead.
 */
import api from './api';

const courseRegistrationApi = {
  // Courses ----------------------------------------------------------------
  listCourses: () => api.get('/course-registration/courses'),
  getCourse: (id) => api.get(`/course-registration/courses/${id}`),
  createCourse: (payload) => api.post('/course-registration/courses', payload),
  updateCourse: (id, payload) => api.put(`/course-registration/courses/${id}`, payload),
  deleteCourse: (id) => api.delete(`/course-registration/courses/${id}`),
  duplicateCourse: (id, payload = {}) => api.post(`/course-registration/courses/${id}/duplicate`, payload),
  sendCourseLink: (id, { phones = [], groupIds = [] } = {}) =>
    api.post(`/course-registration/courses/${id}/send-link`, { phones, groupIds }),
  // Its own endpoint, not a field on updateCourse: that payload rebuilds every
  // course field, so a caller that doesn't know about the default group would
  // clear it.
  setDefaultGroup: (id, groupId) =>
    api.put(`/course-registration/courses/${id}/default-group`, { groupId: groupId || null }),
  // Per-question summary of the course's extra questions (option counts for
  // choice questions). Separate from getCourse: the detail page renders the
  // course header before the aggregate is worth waiting for.
  getCourseAnswerStats: (id) => api.get(`/course-registration/courses/${id}/answer-stats`),

  // Link groups — one shared link bundling several courses -----------------
  listLinkGroups: () => api.get('/course-registration/link-groups'),
  getLinkGroup: (id) => api.get(`/course-registration/link-groups/${id}`),
  createLinkGroup: (payload) => api.post('/course-registration/link-groups', payload),
  updateLinkGroup: (id, payload) => api.put(`/course-registration/link-groups/${id}`, payload),
  deleteLinkGroup: (id) => api.delete(`/course-registration/link-groups/${id}`),
  getLinkGroupAnswerStats: (id) => api.get(`/course-registration/link-groups/${id}/answer-stats`),
  // Every registrant of every course in the bundle, flat — the analytics page's
  // Excel/PDF export. Archived and rejected rows are already left out server-side.
  listLinkGroupRegistrants: (id) => api.get(`/course-registration/link-groups/${id}/registrants`),
  // Every link group with its headline numbers and merged answer split, in one
  // request — the dashboard card renders all of them at once.
  listLinkGroupSummaries: () => api.get('/course-registration/link-groups-summary'),

  // Registrants ------------------------------------------------------------
  listRegistrants: (courseId, status) =>
    api.get(`/course-registration/courses/${courseId}/registrants`, { params: status ? { status } : {} }),
  setRegistrantStatus: (registrantId, status) =>
    api.patch(`/course-registration/registrants/${registrantId}`, { status }),
  // Archiving is its own endpoint, not a status: a converted registrant stays
  // converted (keeping its student + group) and only leaves the queue.
  setRegistrantArchived: (registrantId, archived) =>
    api.patch(`/course-registration/registrants/${registrantId}/archive`, { archived }),
  convert: (courseId, payload) =>
    api.post(`/course-registration/courses/${courseId}/convert`, payload),
};

export default courseRegistrationApi;
